import { useCallback, useEffect, useRef, useState } from 'react';
import LogoutButton from './LogoutButton';
import { setCompanies, setCurrentCompanyId, setRoles } from './slices/configSlice';
import { useDispatch, useSelector } from 'react-redux';
import { CompanyClient } from './api/server';
import { Avatar, Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import { ExtendedClient } from './api/ExtendedClient';
import { useAuth } from './auth/AuthContext';

const Profile = () => {
  const { user, isAuthenticated, role, roles, activeCompanyId, selectCompany } = useAuth();
  const dispatch = useDispatch();
  const currentCompanyId = useSelector((state) => state.globalInfo.currentCompanyId);
  const [companyList, setCompanyList] = useState([]);
  const companyLoadRef = useRef({ userKey: '', promise: null });

  const activateCompany = useCallback(async (companyId) => {
    const result = await selectCompany(companyId);
    dispatch(setCurrentCompanyId(companyId));
    dispatch(setRoles(result.roles?.length ? result.roles : [result.role || 'BusinessOwner']));
  }, [dispatch, selectCompany]);

  useEffect(() => {
    const userKey = isAuthenticated ? user.email : '';
    if (!userKey) {
      return;
    }

    let cancelled = false;

    const loadCompanies = async () => {
      try {
        if (companyLoadRef.current.userKey !== userKey || !companyLoadRef.current.promise) {
          const client = new ExtendedClient(CompanyClient).getClient();
          companyLoadRef.current = {
            userKey,
            promise: client.getCompanies()
          };
        }

        const companies = await companyLoadRef.current.promise;
        if (cancelled) return;

        setCompanyList(companies);
        dispatch(setCompanies(JSON.stringify(companies)));

        if (companies.length > 0) {
          dispatch(setCurrentCompanyId(companies[0].companyId));
        }
      } catch (error) {
        if (companyLoadRef.current.userKey === userKey) {
          companyLoadRef.current = { userKey: '', promise: null };
        }
        console.error(`Error loading user profile: ${error.message}`);
      }
    };

    loadCompanies();
    return () => { cancelled = true; };
  }, [dispatch, isAuthenticated, user.email]);

  useEffect(() => {
    if (!currentCompanyId) {
      return;
    }

    if (activeCompanyId === currentCompanyId) {
      dispatch(setRoles(roles.length ? roles : [role || 'BusinessOwner']));
      return;
    }

    activateCompany(currentCompanyId).catch((error) => {
      console.error(`Error activating company: ${error.message}`);
    });
  }, [activateCompany, activeCompanyId, currentCompanyId, dispatch, role, roles]);

  return isAuthenticated && (
    <Box
      component="header"
      sx={{
        height: { xs: 'auto', sm: 72 },
        minHeight: 72,
        px: { xs: 2, md: 3.5 },
        py: { xs: 1.5, sm: 0 },
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: 1.5,
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        bgcolor: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(14px)',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center', color: 'white', bgcolor: 'primary.main' }}>
            <PhoneInTalkRoundedIcon fontSize="small" />
          </Box>
          <Box>
            <Typography fontWeight={800} color="text.primary" lineHeight={1.1}>Route My Call</Typography>
            <Typography variant="caption" color="text.secondary">Communications portal</Typography>
          </Box>
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent={{ xs: 'space-between', sm: 'flex-end' }} spacing={{ xs: 1, md: 2 }}>
        {companyList.length > 1 && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <BusinessRoundedIcon sx={{ display: { xs: 'none', md: 'block' }, color: 'text.secondary', fontSize: 20 }} />
            <Select
              size="small"
              value={currentCompanyId}
              onChange={(event) => activateCompany(event.target.value)}
              sx={{ minWidth: { xs: 150, sm: 190 }, maxWidth: { xs: 180, sm: 240 }, bgcolor: 'background.paper' }}
            >
              {companyList.map((item) => (
                <MenuItem value={item.companyId} key={item.companyId}>{item.name}</MenuItem>
              ))}
            </Select>
          </Stack>
        )}
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          <Avatar sx={{ width: 34, height: 34, bgcolor: 'secondary.light', color: 'secondary.dark', fontSize: 14, fontWeight: 800 }}>
            {(user.email || '?').charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="body2" fontWeight={650} sx={{ display: { xs: 'none', lg: 'block' }, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {user.email}
          </Typography>
          <LogoutButton />
        </Stack>
      </Stack>
    </Box>
  );
};

export default Profile;
